import { type HttpRequestConfig } from '../lib/utils/httpClient';
import { type CancelReasonFacilitySubmitParams } from './CancelConsultModel';

export interface GeneralType {
  id: number;
  name: string;
}

export interface CancelConsultMetadataResponse {
  cancelReasonList: GeneralType[];
  cancelTechIssueList: GeneralType[];

  // Optional because not all portals have them
  patientAdmittedList?: GeneralType[];
  patientDischargedList?: GeneralType[];
  noLongerRequiredList?: GeneralType[];
}

export interface CompleteConsultParams {
  userId: number;
  consultId: number;
  billable: boolean;
  diagnosis: GeneralType[];
  recommendation: number;
  emCodes: number;
  isSurge: boolean;
  opsReasonForCompletion: string;
  unitOrDepartment: string;
  followUp: number;
}

export interface CompleteDDLData {
  diagnosis: GeneralType[];
  recommendations: GeneralType[];
  emCodes: GeneralType[];
  unitOrDepartment: string[];
  selectedUnitOrDepartment: string;
  followUp: GeneralType[];
}

export interface CompleteConsult {
  billable: boolean;
  diagnosis: number[];
  recommendation: number;
  emCodes: number;
  isSurge: boolean;
  opsReasonForCompletion: string;
  unitOrDepartment: string;
  followUp: number;
}

export interface OnHoldReasonListType {
  id: number;
  name: string;
  subReason: GeneralType[];
}

export interface OnHoldConsultMetadataResponse {
  onHoldReasonList: OnHoldReasonListType[];
}

export interface OnHoldReasonParams {
  consultId: number;
  onHoldConsultReasonId: number;
  onHoldConsultSubReasonId: number;
  onHoldConsultOtherReason: string;
}

export type HttpRequestFn = <T>(config: HttpRequestConfig) => Promise<T>;

export const statusRequestService = (httpRequest: HttpRequestFn) => ({
  getCancelConsultMetadata: async (portalType: string) => {
    const response = await httpRequest<CancelConsultMetadataResponse>({
      url: `integration/consult/cancel/metadata`,
      method: 'GET',
      apiBaseType: 'INTEGRATION',
      headers: {
        portalType: portalType,
      },
    });
    return response;
  },
  saveCancelConsult: async (
    userId: string,
    portalType: string,
    consultId: number,
    payload: CancelReasonFacilitySubmitParams
  ) => {
    const response = await httpRequest<CancelReasonFacilitySubmitParams>({
      method: 'PUT',
      url: `/integration/consult/${consultId}/cancel`,
      headers: { userId, portalType: portalType },
      data: payload,
      apiBaseType: 'INTEGRATION',
    });
    return response;
  },
  getCompleteDropdownData: async (portalType: string, consultId: string) => {
    const response = await httpRequest<CompleteDDLData>({
      url: `/integration/consult/billable/metadata?consultId=${consultId}`,
      method: 'GET',
      apiBaseType: 'INTEGRATION',
      headers: {
        portalType: portalType,
      },
    });
    return response;
  },
  submitCompleteForm: async (
    userId: number,
    consultId: number,
    complete: CompleteConsult,
    portalType: string
  ) => {
    const response = await httpRequest<CompleteConsult>({
      url: `/integration/consult/${consultId}/call-completed?providerId=${userId}`,
      method: 'PUT',
      data: complete,
      apiBaseType: 'INTEGRATION',
      headers: {
        portalType: portalType,
      },
    });
    return response;
  },
  getOnHoldConsultMetadata: async (portalType: string) => {
    const response = await httpRequest<OnHoldConsultMetadataResponse>({
      url: `integration/consult/onHold/metadata`,
      method: 'GET',
      apiBaseType: 'INTEGRATION',
      headers: {
        portalType: portalType,
      },
    });
    return response;
  },
  saveOnHoldConsult: async (
    userId: string,
    portalType: string,
    payload: OnHoldReasonParams
  ) => {
    const response = await httpRequest<OnHoldReasonParams>({
      method: 'PUT',
      url: `/integration/consult/onHold`,
      headers: { userId, portalType: portalType },
      data: payload,
      apiBaseType: 'INTEGRATION',
    });
    return response;
  },
});
